/**
 * PKCE helpers (RFC 7636) for the authorization code flow.
 */

const UNRESERVED = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

/** Random string built from RFC 3986 unreserved characters, suitable for `state` and `code_verifier`. */
export function randomString(length = 64): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => UNRESERVED[byte % UNRESERVED.length]).join('');
}

export interface ICodeChallenge {
  challenge: string;
  method: 'S256' | 'plain';
}

/**
 * Derives the `code_challenge` for a verifier. Uses SHA-256 where WebCrypto is available and falls
 * back to the plain method otherwise, so a sign-in never fails on an exotic WebView.
 */
export async function createCodeChallenge(codeVerifier: string): Promise<ICodeChallenge> {
  if (!crypto.subtle) {
    return { challenge: codeVerifier, method: 'plain' };
  }
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
  return { challenge: base64Url(digest), method: 'S256' };
}

function base64Url(buffer: ArrayBuffer): string {
  const binary = String.fromCharCode(...new Uint8Array(buffer));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
